import { inject } from '@angular/core';
import { of } from 'rxjs';
import {
    CanvasCloseCommand,
    CanvasCommand,
    CanvasDraftUpdateCommand,
    collectCanvasCommands,
    Interceptor,
    PromptResponse
} from '@panaya/ai-chat/domain';
import { AiChatThinkingService, CanvasFrameStore } from '@panaya/ai-chat/features';
import { DraftDocument } from '@panaya/ai-chat/shared';
import { DraftStore } from './draft.store';

/**
 * Streaming interceptor for the draft's `canvas_update` (`content: 'draft'`). It runs after the frame interceptor
 * (`provideCanvasDraft()` is listed after `provideCanvasFrame()`), so a canvas this poll opens is already open and
 * already cleared. A draft update is taken while the canvas is open, and in the poll that closes it, so the draft
 * card shows the final count; after that it is dropped until the next canvas opens. It reads both places a command
 * can sit (top level and inside a `tool_complete` result) and converts nothing: the displays convert each entity's data when
 * they render.
 */
export function createDraftInterceptor(): Interceptor<PromptResponse> {
    const draftStore = inject(DraftStore);
    const frameStore = inject(CanvasFrameStore);
    const thinking = inject(AiChatThinkingService);

    const applyDraft = (draft: DraftDocument): void => {
        draftStore.apply(draft);

        // Set on every poll of a revise turn, after the frame interceptor's "generating", so this text wins. A host
        // names its own text for the phase in its canvas registration's `progress_keys.preparing`.
        if (draftStore.status() === 'filling') {
            thinking.notifyThinkingEvent({
                state: 'thinking',
                translationKey: frameStore.progressKey('preparing', 'aichat.canvas.status_line.preparing')
            });
        }
    };

    return (response: PromptResponse) => {
        const commands = collectCanvasCommands(response);

        if (frameStore.isOpen() || commands.some(isCanvasClose)) {
            commands.filter(isDraftUpdate).forEach((command) => applyDraft(command.draft));
        }

        return of(response);
    };
}

function isCanvasClose(command: CanvasCommand): command is CanvasCloseCommand {
    return command.type === 'canvas_close';
}

/** A `canvas_update` for the draft family; another content family's update is not this interceptor's. */
function isDraftUpdate(command: CanvasCommand): command is CanvasDraftUpdateCommand {
    return command.type === 'canvas_update' && command.content === 'draft';
}
